import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, MobileBottomNav } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';
import { 
  User, Shield, Bell, Moon, Globe, HelpCircle, 
  LogOut, ChevronRight, CreditCard, Lock, ChevronLeft, Save
} from 'lucide-react';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [profileForm, setProfileForm] = useState({ full_name: user?.full_name || '', email: user?.email || '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setTimeout(() => { setSaving(false); toast.success('Profile updated!'); setActiveSection(null); }, 1000);
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPw !== passwordForm.confirm) { toast.error("Passwords don't match"); return; }
    if (passwordForm.newPw.length < 6) { toast.error("Min 6 characters"); return; }
    setSaving(true);
    try {
      await api.post('/auth/request-password-reset', { email: user?.email });
      toast.success('Password reset code sent to your notifications!');
      setActiveSection(null);
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const settingsSections = [
    { id: 'profile', icon: User, label: 'Profile', desc: 'Edit your personal information' },
    { id: 'security', icon: Shield, label: 'Security', desc: 'Password and 2FA settings' },
    { id: 'subscription', icon: CreditCard, label: 'Subscription', desc: user?.subscription_tier === 'free' ? 'Upgrade your plan' : `${user?.subscription_tier} plan` },
    { id: 'kyc', icon: Lock, label: 'KYC Verification', desc: 'Verify your identity' },
    { id: 'notifications', icon: Bell, label: 'Notifications', desc: 'Manage your notifications' },
    { id: 'help', icon: HelpCircle, label: 'Help Center', desc: 'FAQs and guides' },
  ];

  const handleSectionClick = (id) => {
    if (id === 'kyc') { navigate('/kyc'); return; }
    if (id === 'subscription') { toast.info('Subscription plans: Free / $5 Green / $10 Gold'); return; }
    if (id === 'notifications') { toast.info('Notification preferences coming soon'); return; }
    if (id === 'help') { toast.info('Visit our chatbot for help!'); return; }
    setActiveSection(id);
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0 overflow-x-hidden">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto px-3 py-6 w-full">
        {activeSection === 'profile' ? (
          <div>
            <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6">
              <ChevronLeft className="h-4 w-4" /> Back to Settings
            </button>
            <h1 className="text-2xl font-black text-white mb-6" style={{ fontFamily: 'Outfit' }}>Edit <span className="text-[#D4AF37]">Profile</span></h1>
            <div className="card-dark space-y-4">
              <div>
                <Label className="text-white text-sm font-semibold">Full Name</Label>
                <Input value={profileForm.full_name} onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})} className="input-dark mt-1" data-testid="settings-name-input" />
              </div>
              <div>
                <Label className="text-white text-sm font-semibold">Email</Label>
                <Input value={profileForm.email} disabled className="input-dark mt-1 opacity-50" />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <Button onClick={handleSaveProfile} className="btn-primary w-full" disabled={saving} data-testid="save-profile-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : activeSection === 'security' ? (
          <div>
            <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6">
              <ChevronLeft className="h-4 w-4" /> Back to Settings
            </button>
            <h1 className="text-2xl font-black text-white mb-6" style={{ fontFamily: 'Outfit' }}>Security <span className="text-[#D4AF37]">Settings</span></h1>
            <div className="card-dark space-y-4">
              <div>
                <Label className="text-white text-sm font-semibold">Current Password</Label>
                <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})} className="input-dark mt-1" placeholder="Enter current password" data-testid="current-password" />
              </div>
              <div>
                <Label className="text-white text-sm font-semibold">New Password</Label>
                <Input type="password" value={passwordForm.newPw} onChange={(e) => setPasswordForm({...passwordForm, newPw: e.target.value})} className="input-dark mt-1" placeholder="Min 6 characters" data-testid="new-password" />
              </div>
              <div>
                <Label className="text-white text-sm font-semibold">Confirm New Password</Label>
                <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})} className="input-dark mt-1" placeholder="Confirm new password" data-testid="confirm-password" />
              </div>
              <Button onClick={handleChangePassword} className="btn-primary w-full" disabled={saving} data-testid="change-password-btn">
                <Lock className="h-4 w-4 mr-2" /> {saving ? 'Processing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black uppercase text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              <span className="text-[#D4AF37]">Settings</span>
            </h1>
            <div className="card-dark mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center text-xl font-bold text-[#1A1A1A]">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-white truncate">{user?.full_name || 'User'}</h2>
                  <p className="text-gray-400 text-sm truncate">{user?.email}</p>
                  <span className="badge-gold text-xs mt-1 inline-block capitalize">{user?.subscription_tier || 'Free'} Plan</span>
                </div>
              </div>
            </div>
            <div className="card-dark p-0 divide-y divide-[#2D2D2D] mb-6">
              {settingsSections.map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.id} onClick={() => handleSectionClick(item.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#2D2D2D] transition-colors"
                    data-testid={`settings-${item.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#2D2D2D] flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm">{item.label}</div>
                      <div className="text-gray-500 text-xs truncate">{item.desc}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
            <Button onClick={handleLogout} className="w-full bg-red-500/10 border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all py-3" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Log Out
            </Button>
          </>
        )}
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default SettingsPage;
